import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Team from "./pages/Team";
import Customers from "./pages/Customers";
import Campaigns from "./pages/Campaigns";
import Passes from "./pages/Passes";
import NewPass from "./pages/passes/NewPass";
import EditPass from "./pages/passes/EditPass";
import Marketing from "./pages/Marketing";
import Integrations from "./pages/Integrations";
import PublicReferral from "./pages/PublicReferral";
import Profile from "./pages/Profile";
import { QueryClientProvider } from "@tanstack/react-query";
import StoreCheckWrapper from "./components/StoreCheckWrapper";
import ManagerOnlyRoute from "./components/ManagerOnlyRoute";
import CreateStore from "./pages/stores/new";
import { StoreProvider } from "./contexts/StoreContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import NewForm from "./pages/forms/new";
import EditForm from "./pages/forms/edit";
import PublicForm from "./pages/PublicForm";
import NewFlyer from "./pages/flyers/new";
import EditFlyer from "./pages/flyers/edit";
import CustomerShow from "./pages/CustomerShow";
import Pricing from "./pages/Pricing";
import Settings from "./pages/Settings";
import Forms from "./pages/Forms";
import Flyers from "./pages/Flyers";
import Notifications from "./pages/Notifications";
import Setup from "./pages/Setup";
import ResetPassword from "./pages/ResetPassword";
import EditStore from "./components/EditStore";
import { queryClient } from "./config/queryClient";
import TermsOfService from "./pages/TermsOfService";
import Club from "./pages/Club";
import NewClub from "./pages/club/new";

import { useEffect } from "react";
import {
  NotificationProvider,
  useNotification,
} from "./contexts/NotificationContext";
import { setupAxiosErrorHandler } from "./api/axios";
import { PlanAlertProvider } from "./contexts/PlanAlertContext";
import IntegrationConfigPage from "./pages/integrations/IntegrationConfigPage";
import { useSupportChatDeepLink } from "./hooks/useSupportChatDeepLink";

function AppWithProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <StoreProvider>
              <PlanAlertProvider>
                <AppWithInterceptors />
              </PlanAlertProvider>
            </StoreProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function AppWithInterceptors() {
  const { showNotification } = useNotification();
  const { t } = useLanguage();

  // Global, so an email CTA can point at any route with ?support=chat.
  useSupportChatDeepLink();

  useEffect(() => {
    // Setup axios interceptors with notification handler
    setupAxiosErrorHandler(t, showNotification);
  }, [t, showNotification]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/f/:formId" element={<PublicForm />} />
      <Route path="/r/:formId/:customerId" element={<PublicReferral />} />
      <Route path="/club/new" element={<NewClub />} />

      <Route
        path="/terms-of-service"
        element={
          <ProtectedRoute>
            <TermsOfService />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        element={
          <ProtectedRoute>
            <StoreCheckWrapper>
              <Layout />
            </StoreCheckWrapper>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/pricing"
          element={
            <ManagerOnlyRoute>
              <Pricing />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/team" element={<Team />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route
          path="/forms/new"
          element={
            <ManagerOnlyRoute>
              <NewForm />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/flyers/new"
          element={
            <ManagerOnlyRoute>
              <NewFlyer />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/passes" element={<Passes />} />
        <Route
          path="/passes/new"
          element={
            <ManagerOnlyRoute>
              <NewPass />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/passes/:id/edit"
          element={
            <ManagerOnlyRoute>
              <EditPass />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/profile" element={<Profile />} />
        <Route path="/stores/new" element={<CreateStore />} />
        <Route
          path="/forms/:id/edit"
          element={
            <ManagerOnlyRoute>
              <EditForm />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/stores/:id/edit"
          element={
            <ManagerOnlyRoute>
              <EditStore />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/flyers/:id/edit"
          element={
            <ManagerOnlyRoute>
              <EditFlyer />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/customers/:ref" element={<CustomerShow />} />
        <Route
          path="/settings"
          element={
            <ManagerOnlyRoute>
              <Settings />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/forms" element={<Forms />} />
        <Route path="/flyers" element={<Flyers />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path="/integrations"
          element={
            <ManagerOnlyRoute>
              <Integrations />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/club"
          element={
            <ManagerOnlyRoute>
              <Club />
            </ManagerOnlyRoute>
          }
        />

        <Route
          path="/integration/:id/configure"
          element={
            <ManagerOnlyRoute>
              <IntegrationConfigPage />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/setup"
          element={
            <ManagerOnlyRoute>
              <Setup />
            </ManagerOnlyRoute>
          }
        />
        <Route
          path="/setup/:step"
          element={
            <ManagerOnlyRoute>
              <Setup />
            </ManagerOnlyRoute>
          }
        />
        <Route path="/marketing" element={<Marketing />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <NotificationProvider>
      <AppWithProviders />
    </NotificationProvider>
  );
}

export default App;
